import { FilterFilled, PhoneTwoTone, PlusOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Button,
  Card,
  DatePicker,
  Image,
  Select,
  Space,
  Tag,
} from 'antd';
import { Option } from 'antd/es/mentions';
import dayjs from 'dayjs';
import { capitalize, debounce, map } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { PhoneSlash } from '../../assets/svg';
import {
  BACKEND_DATE_FORMAT,
  DATE_FILTERS,
  DATE_TIME_FORMAT,
  DEFAULT_DATE_FORMAT,
  HOUR_LOGS_DATE_TIME_FORMAT,
  INCIDENT_REPORT_TABLE_FIELDS,
  INCIDENT_STATUS,
  MODULES_KEY,
  PERMISSION_OBJECT,
  ROLE_KEYS,
  ROUTES_MODULES_KEY,
  SEVERITY_STATUS,
  USER,
} from '../../common/constants';
import CommonSelect from '../../components/CommonSelect';
import CommonTable from '../../components/CommonTable';
import VerifyPermissions from '../../components/VerifyPermissions';
import {
  antdSortOrder,
  useTableQueryParams,
} from '../../hooks/useTableQueryParams';
import { INCIDENT_MASTERS } from '../incidentMaster/graphql/Queries';
import CommonPreviewTable from './components/CommonPreviewTable';
import IncidentModal from './components/IncidentModal';
import { UPDATE_INCIDENT_REPORT } from './graphql/Mutations';
import { DESIGNATIONS, INCIDENTS_LISTING } from './graphql/Queries';

const { RangePicker } = DatePicker;

const buildIncidentReportFilter = (params) => ({
  skip: (params?.page - 1) * params?.pageSize,
  limit: params?.pageSize,
  sortField: params?.sortField,
  sortOrder: params?.sortOrder,
  search: params?.search,
  status: params?.status,
  severity: params?.severity,
  incidentMasterId: params?.incidentMasterId,
  incidentDateFilter: {
    startDate: params?.incidentDate?.from || null,
    endDate: params?.incidentDate?.to || null,
  },
  createdAtFilter: {
    startDate: params?.createdAt?.from || null,
    endDate: params?.createdAt?.to || null,
  },
  updatedAtFilter: {
    startDate: params?.updatedAt?.from || null,
    endDate: params?.updatedAt?.to || null,
  },
});

function IncidentReport() {
  // eslint-disable-next-line no-undef
  const user = JSON.parse(localStorage.getItem(USER));
  const history = useHistory();
  const { params, setParams } = useTableQueryParams({
    fields: INCIDENT_REPORT_TABLE_FIELDS,
  });
  const [userSearch, setUserSearch] = useState();
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [filterVisible, setFilterVisible] = useState({
    statusFilterVisible: false,
    severityFilterVisible: false,
    categoryFilterVisible: false,
  });
  const [selectedStatus, setSelectedStatus] = useState(params?.status);
  const [masterId, setMasterId] = useState(params?.incidentMasterId);
  const [isEdit, setIsEdit] = useState(false);
  const [visible, setVisible] = useState(false);
  const [toyImages, setToyImages] = useState([]);

  const isCreatePermission = VerifyPermissions({
    modulekey: MODULES_KEY?.INCIDENT_REPORT,
    allowedPermissions: [PERMISSION_OBJECT?.create],
  });
  const disabledDate = (current) =>
    current && current?.isAfter(dayjs().endOf('day'));

  const [executeQuery, { loading, data }] = useLazyQuery(INCIDENTS_LISTING, {
    fetchPolicy: 'network-only',
    onError: () => {},
  });

  const [executeUpdateQuery] = useMutation(UPDATE_INCIDENT_REPORT, {
    onCompleted: () => {
      setModalVisible(false);
      executeQuery({
        variables: { filters: buildIncidentReportFilter(params) },
      });
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error :', error);
    },
  });

  useEffect(() => {
    executeQuery({ variables: { filters: buildIncidentReportFilter(params) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleSearch = debounce((value) => {
    setParams({
      search: value?.length > 0 ? value?.toString()?.trim()?.split(',') : [],
      page: 1,
    });
  }, 500);

  const onChange = (pagination, _filters, sorter) => {
    setParams({
      page: pagination?.pageSize !== params?.pageSize ? 1 : pagination?.current,
      pageSize: pagination?.pageSize,
      sortField: sorter?.field,
      sortOrder: sorter?.order === 'ascend' ? 'ASC' : 'DESC',
    });
  };

  const handleEditSubmit = async (formData) => {
    try {
      await executeUpdateQuery({
        variables: {
          data: {
            ...formData,
            reasonForRejection: ' ',
            reasonForResolving: ' ',
            mom: formData?.mom,
            scheduledOn: dayjs(formData?.scheduledOn)?.format(
              HOUR_LOGS_DATE_TIME_FORMAT,
            ),
          },
          where: selectedIncident?.id,
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error :', error);
    }
  };
  const handleFilter = () => {
    setParams({ status: selectedStatus, incidentMasterId: masterId, page: 1 });
    setFilterVisible({
      ...filterVisible,
      statusFilterVisible: false,
      categoryFilterVisible: false,
    });
  };

  const handleSeverityFilter = (value) => {
    setParams({ severity: value, page: 1 });
  };

  const handleReset = () => {
    setMasterId(null);
    setSelectedStatus(null);
    setParams({ status: null, incidentMasterId: null, page: 1 });
  };

  const handleIncidentDateFilter = (dates) => {
    const [startDate, endDate] = dates || [];
    setParams({
      incidentDate: {
        from: startDate ? startDate?.format(BACKEND_DATE_FORMAT) : null,
        to: endDate ? endDate?.format(BACKEND_DATE_FORMAT) : null,
      },
      page: 1,
    });
  };

  const handleCreatedAtFilter = (dates) => {
    const [startDate, endDate] = dates || [];
    setParams({
      createdAt: {
        from: startDate ? startDate?.format(BACKEND_DATE_FORMAT) : null,
        to: endDate ? endDate?.format(BACKEND_DATE_FORMAT) : null,
      },
      page: 1,
    });
  };

  const handleUpdatedAtFilter = (dates) => {
    const [startDate, endDate] = dates || [];
    setParams({
      updatedAt: {
        from: startDate ? startDate?.format(BACKEND_DATE_FORMAT) : null,
        to: endDate ? endDate?.format(BACKEND_DATE_FORMAT) : null,
      },
      page: 1,
    });
  };

  const handleDateFilter = (filterName, dates) => {
    switch (filterName) {
      case DATE_FILTERS.INCIDENT_DATE:
        handleIncidentDateFilter(dates);
        break;
      case DATE_FILTERS.CREATED_AT:
        handleCreatedAtFilter(dates);
        break;
      case DATE_FILTERS.UPDATED_AT:
        handleUpdatedAtFilter(dates);
        break;
      default:
        break;
    }
  };

  const handleUserSearch = debounce((value) => {
    setUserSearch(value);
  }, 500);

  const getCategoryFilterPops = () => ({
    filterDropdownOpen: filterVisible?.categoryFilterVisible,
    onFilterDropdownOpenChange: (visibleFilter) =>
      setFilterVisible({
        ...filterVisible,
        categoryFilterVisible: visibleFilter,
      }),
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <CommonSelect
            allowClear
            placeholder="Select Category"
            showSearch
            query={INCIDENT_MASTERS}
            onSearch={handleUserSearch}
            responsePath="incidentMasters.data"
            valuePath="id"
            labelPath="name"
            fetchPolicy="network-only"
            variables={{
              filters: { search: userSearch, statusFilter: 'ACTIVE' },
            }}
            value={masterId}
            resolveMissingSelected
            onChange={(e) => {
              if (e) {
                setMasterId(e);
              } else {
                setMasterId();
              }
            }}
            useEffectDeps={[userSearch]}
            virtual={false}
          />
          <Space direction="horizontal">
            <Button size="small" onClick={handleFilter}>
              Apply
            </Button>
            <Button
              danger
              size="small"
              onClick={() => {
                handleReset();
                setFilterVisible({
                  ...filterVisible,
                  categoryFilterVisible: false,
                });
              }}
            >
              Reset
            </Button>
          </Space>
        </Space>
      </div>
    ),
    filterIcon: () => (
      <FilterFilled
        className={params?.incidentMasterId ? 'filter-filled-data' : ''}
      />
    ),
  });

  const getStatusFilterProps = () => ({
    filterDropdownOpen: filterVisible?.statusFilterVisible,
    onFilterDropdownOpenChange: (visibleFilter) =>
      setFilterVisible({
        ...filterVisible,
        statusFilterVisible: visibleFilter,
      }),
    filterDropdown: () => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <Select
            allowClear
            className="w-150 mr-8 mb-5"
            placeholder="Sort By Status"
            value={selectedStatus}
            onChange={(e) => {
              if (e) {
                setSelectedStatus(e);
              } else {
                setSelectedStatus();
              }
            }}
          >
            {map(INCIDENT_STATUS, (status) => (
              <Option key={status} value={status} label={status}>
                {capitalize(status)}
              </Option>
            ))}
          </Select>
          <Space direction="horizontal">
            <Button size="small" onClick={handleFilter}>
              Apply
            </Button>
            <Button
              danger
              size="small"
              onClick={() => {
                handleReset();
                setFilterVisible({
                  ...filterVisible,
                  statusFilterVisible: false,
                });
              }}
            >
              Reset
            </Button>
          </Space>
        </Space>
      </div>
    ),
    filterIcon: () => (
      <FilterFilled
        className={params?.status ? 'filter-filled-data' : ''}
      />
    ),
  });

  const getDateFilterProps = (filterName, dateRangeParam) => ({
    filterDropdown: ({ confirm }) => (
      <div className="columnFilter" onKeyDown={(e) => e?.stopPropagation()}>
        <Space direction="vertical">
          <RangePicker
            value={
              dateRangeParam?.from && dateRangeParam?.to
                ? [
                    dayjs(dateRangeParam?.from, BACKEND_DATE_FORMAT),
                    dayjs(dateRangeParam?.to, BACKEND_DATE_FORMAT),
                  ]
                : null
            }
            onChange={(dates) => {
              handleDateFilter(filterName, dates);
              confirm();
            }}
            onClear={() => {
              handleDateFilter(filterName, []);
              confirm();
            }}
            disabledDate={disabledDate}
          />
        </Space>
      </div>
    ),
    filterIcon: () => (
      <FilterFilled
        className={
          dateRangeParam?.from || dateRangeParam?.to ? 'filter-filled-data' : ''
        }
      />
    ),
  });

  const getColorForSeverity = (severity) => {
    switch (severity) {
      case SEVERITY_STATUS.MEDIUM:
        return 'severity-medium';
      case SEVERITY_STATUS.LOW:
        return 'severity-low';
      default:
        return 'severity-high';
    }
  };

  const handleRowClick = (record) => {
    const handleRow = () => {
      setSelectedIncident(record);
      setModalVisible(true);
      setIsEdit(true);
    };

    return {
      onClick: handleRow,
    };
  };

  const columns = [
    {
      title: 'Reported By',
      dataIndex: 'raisedByUser',
      key: 'raisedByUser',
      render: (value, record) => (
        <div className="incident-severity-indicator">
          <span
            className={`severity-color ${getColorForSeverity(
              record?.severity,
            )}`}
          />
          <div className="incident-details">
            {value?.displayName || value?.firstName}
            <p className="name-column">
              [
              {`${record?.raisedByUser?.department?.name}>${record?.raisedByUser?.jobTitle}`}
              ]
            </p>
          </div>
        </div>
      ),
    },
    {
      title: 'Reporting On',
      dataIndex: 'reportingForUser',
      key: 'reportingForUser',
      render: (value, record) => (
        <>
          <div className="d-flex discussion-required">
            <div className="is-discussion-required" style={{ width: '30px' }}>
              {record?.isDiscussionRequired ? (
                <PhoneTwoTone size="small" />
              ) : (
                <PhoneSlash />
              )}
            </div>
            <div>
              {value?.displayName || value?.firstName}
              <p className="name-column">
                [
                {`${record?.reportingForUser?.department?.name}>${record?.reportingForUser?.jobTitle}`}
                ]
              </p>
            </div>
          </div>
        </>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'incidentMaster',
      key: 'incidentMaster',
      ...getCategoryFilterPops(),
      render: (record) => (record ? record?.name : 'N/A'),
    },
    {
      title: 'Task/Project',
      dataIndex: 'projectName',
      key: 'projectName',
      render: (text) => capitalize(text),
    },
    {
      title: 'IMAGES',
      dataIndex: 'imageUrls',
      key: 'imageUrls',
      ellipsis: true,
      width: 100,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <span className="image-preview-wrapper">
          {record?.imageUrls?.length > 0 ? (
            <>
              <Image
                preview={{
                  visible: false,
                }}
                width={20}
                height={20}
                src={record?.imageUrls?.[0]?.url}
                onClick={(e) => {
                  e?.stopPropagation();
                  setVisible(true);
                  setToyImages(record?.imageUrls);
                }}
                className="pointer image-preview-icon"
              />
            </>
          ) : (
            <span>No Images</span>
          )}
          {record?.imageUrls?.length > 1
            ? ` + ${record?.imageUrls?.length - 1}`
            : ''}
        </span>
      ),
    },
    {
      title: 'Incident Date',
      dataIndex: 'date',
      key: 'date',
      render: (value) => dayjs(value)?.format(DEFAULT_DATE_FORMAT),
      ...getDateFilterProps(DATE_FILTERS.INCIDENT_DATE, params?.incidentDate),
    },
    {
      title: 'Reported Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      sortOrder: antdSortOrder(params, 'createdAt'),
      render: (value) => dayjs(value)?.format(DATE_TIME_FORMAT),
      ...getDateFilterProps(DATE_FILTERS.CREATED_AT, params?.createdAt),
    },
    {
      title: 'Last Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: true,
      sortOrder: antdSortOrder(params, 'updatedAt'),
      render: (value) => dayjs(value)?.format(DATE_TIME_FORMAT),
      ...getDateFilterProps(DATE_FILTERS.UPDATED_AT, params?.updatedAt),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      ...getStatusFilterProps(),
      render: (status) => {
        const formattedStatus = status || INCIDENT_STATUS.PENDING;
        let color;
        if (formattedStatus === INCIDENT_STATUS.RESOLVED) {
          color = 'green';
        } else if (formattedStatus === INCIDENT_STATUS.INITIATED) {
          color = 'blue';
        } else if (formattedStatus === INCIDENT_STATUS.REJECTED) {
          color = 'red';
        } else {
          color = 'yellow';
        }
        return <Tag color={color}>{formattedStatus}</Tag>;
      },
    },
  ];
  const renderMaxTag = (omittedValues) => {
    if (omittedValues?.length === 0) {
      return null;
    }
    return <span>+ {omittedValues?.length} more...</span>;
  };

  return (
    <div className="table-card-page">
      <CommonPreviewTable
        visible={visible}
        setVisible={setVisible}
        images={toyImages}
        setImages={setToyImages}
      />
      <Card
        className="ant-body-scroll"
        title="Incident Reports"
        extra={
          <>
            <span className="incident-report-select">
              <Space>
                {![ROLE_KEYS?.SUPER_ADMIN, ROLE_KEYS?.ADMIN]?.includes(
                  user?.role,
                ) &&
                  isCreatePermission && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        history?.push({
                          pathname: `${ROUTES_MODULES_KEY?.INCIDENT_REPORT}/create`,
                        })
                      }
                    >
                      Create Incident
                    </Button>
                  )}
                <Select
                  className="severity-select"
                  placeholder="Select The Severity"
                  allowClear
                  onChange={handleSeverityFilter}
                  value={params?.severity}
                >
                  {map(SEVERITY_STATUS, (status) => (
                    <Option key={status} value={status} label={status}>
                      {capitalize(status)}
                    </Option>
                  ))}
                </Select>
                <CommonSelect
                  className="incident-select"
                  mode="tags"
                  placeholder="Search Name, Department, Designation"
                  showSearch
                  query={DESIGNATIONS}
                  onChange={handleSearch}
                  responsePath="designations.designations"
                  valuePath="jobTitle"
                  labelPath="jobTitle"
                  uniqByVariable="jobTitle"
                  fetchPolicy="network-only"
                  onClear={() => setParams({ search: [], page: 1 })}
                  value={params?.search}
                  maxTagCount={3}
                  maxTagPlaceholder={renderMaxTag}
                  virtual={false}
                />
              </Space>
            </span>
          </>
        }
      >
        <CommonTable
          onRow={handleRowClick}
          size="small"
          bordered
          rowClassName="pointer"
          columns={columns}
          dataSource={data?.incidentReportsAdmin?.data}
          loadingData={loading}
          onChange={onChange}
          paginationConfig={{
            current: params?.page,
            pageSize: params?.pageSize,
            total: data?.incidentReportsAdmin?.count,
          }}
          rowKey="id"
        />
      </Card>
      {modalVisible && (
        <IncidentModal
          visible={modalVisible}
          onCancel={() => setModalVisible(false)}
          onSubmit={handleEditSubmit}
          incident={selectedIncident}
          mode={isEdit}
        />
      )}
    </div>
  );
}

export default IncidentReport;
